<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import CardItem from './CardItem.vue';
import type { CardItemType } from './CardItem.vue';

export interface Props {
  title?: string,
  tooltip?: string,
  list?: CardItemType[],
  isLabelEllipsis?: boolean,
  isContentEllipsis?: boolean,
  noValue?: boolean,
  noDataText?: string,
  noDataIcon?: string,
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  tooltip: '',
  list: () => [],
  isLabelEllipsis: false,
  noValue: false,
  isContentEllipsis: true,
  noDataText: '',
  noDataIcon: 'folder-open',
});

const { t } = useI18n();
const slots = useSlots();

const pNoDataText = computed(() => {
  return props.noDataText || t('players.info.no_data');
});
const listNotEmpty = computed(() => !slots.cardItem && props.list.length > 0);
</script>

<template>
  <div class="card-block">
    <div class="t:flex t:gap-8">
      <slot name="blockTitle">
        <CardTitle
          :title="title"
          :tooltip="tooltip"
          class="t:w-full"
        >
          <slot name="action" />
        </CardTitle>
      </slot>
      <slot name="actionOuter" />
    </div>
    <div class="t:space-y-8">
      <slot name="prependBlock" />
      <div class="t:px-8">
        <slot name="cardItem">
          <template v-if="listNotEmpty">
            <CardItem
              v-for="(item, index) in list"
              :key="index"
              :item="item"
              :no-border="index === 0"
              :is-content-ellipsis="isContentEllipsis"
              :is-label-ellipsis="isLabelEllipsis"
              :no-value="noValue"
            />
          </template>
          <template v-else>
            <div class="card-block__empty">
              <UiIcon
                :size="32"
                :name="noDataIcon"
                substyle="fal"
                color="inherit"
                class="t:mt-24 t:mb-8"
              />
              {{ pNoDataText }}
            </div>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.card-block{
  min-width: 260px;
  width: 100%;

  &__empty{
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    font-size: 20px;
    color: var(--text-body-tertiary);
    width: 100%;
  }
}
</style>
